import Vue from 'vue'
import Vuex from 'vuex'
import AparênciaPadrão from './AparênciaPadrão'
import { fastlane, canalContato, abrirConexãoWebsocket, ensureConnectedToDepartment } from './Socket'
import axios from 'axios'
import { buildChatStore } from '@Shared/Chat/Store'
import { Flash } from '@App/Store/Flash'

Vue.use(Vuex)

export const AMBIENTE_EDITOR = 'editor'
export const AMBIENTE_WEBSITE = 'website'

let chatStore = buildChatStore({ fastlane })

export const store = new Vuex.Store({
    modules: { flash: Flash },
    state: {
        ...chatStore.state,

        /**
         * URL que vamos usar para comunicação com servidor.
         */
        endpoint: window.__tecliaEndpoint || 'https://teclia.com',
        fastlaneEndpoint: window.__tecliaFastlaneEndpoint || 'https://teclia.com',

        /**
         * Quando o widget está rodando dentro do editor, algumas coisas mudam. O layout muda ligereiramente (h-screen, por exemplo)
         * e os eventos são ignorados para que não seja possível iniciar uma conversa.
         */
        ambiente: AMBIENTE_WEBSITE,

        /**
         * O widget está rodando no modo fullscreen?
         */
        fullscreen: false,

        /**
         * O widget está rodando em mobile?
         */
        mobile: false,

        /**
         * Token do webmessenger que está berto.
         */
        webMessengerToken: null,

        /**
         * Em qual etapa (views) do widget.
         */
        etapa: 'convite',

        /**
         * Controle do evento de "Digitando..." recebido dos operadores do sistema.
         */
        flashTyping: {},

        /**
         * O convite está visível?
         */
        inviteVisible: true,

        /**
         * Quantidade máxima de salas que o contato pode ter simultaneamente.
         */
        maxAmountOfActiveRooms: 2,

        /**
         * Quantidade máxima de salas que o contaot pode ter simultaneamento de um mesmo grupo.
         */
        maxActiveTicketsPerGroup: null,

        /**
         * Atributo de agrupamento dos tickets para verificar `max_active_tickets_per_group`.
         */
        groupTicketsBy: null,

        /**
         * Permitir abertura de atendimento somente por chamada de API.
         */
        onlyAllowNewChatRoomsFromAPICalls: false,

        /**
         * Qual etapa devemos abrir ao maximizar o convite.
         */
        maximizeStep: null,

        /**
         * O cliente está com a janela focada? Nós precisamos fazer esse controle para controler visualização de mensagens.
         */
        focoJanela: true,

        /**
         * Essa flag controla o período de requisição para abertura do atendimento. O problema de UI que isso resolve é
         * entre o momento que o usuário digita a primeira mensagem, que irá abrir o atendimento, até o momento que a mensagem
         * é mostrada na tela.
         */
        openingNewRoom: false,

        /**
         * Status da conexão websocke.
         * False quer dizer que estamos desconectados.
         */
        websocketConnectionStatus: true,

        /**
         * Personalização da aparência do widget, como imagem do convite e cor primária.
         */
        aparência: AparênciaPadrão,

        /**
         * Lista de departamentos que a pessoa pode iniciar conversas.
         */
        departamentos: [],

        /**
         * Timezone do space que contém esse web messenger
         */
        timezoneOffset: 0,

        /**
         * Horário de trabalho do workspce que contém esse web messenger.
         */
        workingHours: [],

        /**
         * Disponibilidade dos departamentos, carregado por demanda ao visitar a home.
         */
        departmentsAvailability: null,

        /**
         * Termo pesquisado na base de conhecimento.
         */
        search: '',

        /**
         * Conhecimentos que foram encontrados a partir de uma pesquisa na base de conhecimento.
         */
        questionsFoundFromKnowledgeDatabaseSearch: null,

        /**
         * ID do conhecimento selecionado pelo usuário.
         */
        selectedQuestionId: null,

        /**
         * Configuração do widget, como o token e departamento pré-selecionado.
         */
        configuração: window._tecliaWebMessengerParams || {},

        /**
         * Posição da fila por sala.
         */
        queuePositions: {},

        /**
         * Informações que sabemos sobre a pessoa que está acessando o site (como nome e e-mail).
         */
        perfil: {},

        /**
         * Registro do contato que representa a pessoa que está acessando o site.
         */
        contato: {},

        /**
         * Perfis dos usuários que estamos conversando, usado para mostrar nome e avatar no cabeçalho.
         */
        perfisDosUsers: {},
        loadingUserProfiles: {},
    },
    getters: {
        ...chatStore.getters,

        estamosEmHorarioDeTrabalho: state => {
            let workingHours = state.workingHours
            if (!workingHours || workingHours.length == 0) return false

            let workspaceOffset = state.timezoneOffset / 60
            let now = new Date()
            let myOffset = now.getTimezoneOffset() * -1

            const padLeft = num => (num < 10 ? '0' + num.toString() : num.toString())

            // Se o dia estiver desabilitado, não estamos em horário de trabalho.
            if (workingHours[now.getDay()] == 0) return false

            let offsetDiff = myOffset - workspaceOffset
            let hoursDiff = parseInt(offsetDiff / 60)
            let minutesDiff = offsetDiff - hoursDiff * 60

            let currentTime = padLeft(now.getHours() + hoursDiff) + ':' + padLeft(now.getMinutes() + minutesDiff)

            let day = state.workingHours[now.getDay()]

            if (currentTime >= day['início_1'] && currentTime < day['final_1']) return true
            if (currentTime >= day['início_2'] && currentTime < day['final_2']) return true

            return false
        },

        corPrimária: state => {
            return state.aparência.primaryColor || '#4299E1'
        },

        posição: state => {
            return state.aparência.position
        },

        tamanhoDoConvite: state => {
            return parseInt(state.aparência.inviteSize)
        },

        perfil: state => canal => {
            return state.perfisDosUsers[canal]
        },

        notificaçõesHabilitadas: state => room_id => {
            return true
        },

        activeRooms: state => {
            return state.salasByTab['chat'].filter(room => {
                return room.status != 'closed'
            })
        },

        /**
         * O contato pode iniciar mais um atendimento?
         */
        canStartAnotherRoom: (state, getters) => {
            return getters.activeRooms.length < state.maxAmountOfActiveRooms && !state.onlyAllowNewChatRoomsFromAPICalls
        },

        /**
         * Encontra o primeiro id do usuário que atendeu o atendimento.
         */
        roomUserId: (state, getters) => room_id => {
            let room = getters.findRoomById(room_id)
            if (!room) return null
            if (!room.atributos_extras) return null
            if (!room.atributos_extras.users_ids) return null
            return room.atributos_extras.users_ids[0]
        },

        activeRooms: state => {
            return state.salasByTab['chat'].filter(room => {
                return room.status != 'closed'
            })
        },

        /**
         * Lista de departamentos disponíveis para o atendimento.
         */
        availableDepartments(state) {
            if (state.perfil.department_code) {
                let dept = state.departamentos.find(d => d.code == state.perfil.department_code)
                if (!dept) return []

                if (state.departmentsAvailability && state.departmentsAvailability[dept.id]) {
                    return [dept]
                }

                return []
            }

            return state.departamentos.filter(dept => {
                return state.departmentsAvailability && state.departmentsAvailability[dept.id]
            })
        },

        /**
         * Encontra o registro do departamento pelo token.
         */
        findDepartamentByToken: state => token => {
            let departamentos = state.departamentos || []
            return departamentos.find(d => d.token == token)
        },

        /**
         * Encontra o registro do departamento pelo ID.
         */
        findDepartamentById: state => id => {
            let departamentos = state.departamentos || []
            return departamentos.find(d => d.id == id)
        },

        /**
         * Posição da fila do atendimento.
         */
        queuePosition: state => roomId => {
            let position = state.queuePositions[roomId] || 0
            if (position + 1 < 10) return '0' + (position + 1)
            return position
        },
    },
    mutations: {
        ...chatStore.mutations,

        /**
         * Atualiza o token desse web messenger.
         */
        atualizarWebMessengerToken(state, token) {
            state.webMessengerToken = token
        },

        /**
         * Mutation chamada uma única vez quando o widget for aberto no editor.
         *
         * Quando o widget está rodando dentro do editor, algumas coisas mudam. O layout muda ligereiramente (h-screen, por exemplo)
         * e os eventos são ignorados para que não seja possível iniciar uma conversa.
         */
        atualizarAmbiente(state, ambiente) {
            state.ambiente = ambiente
        },

        /**
         * Atualiza o estado de foco da janela para true (foco) ou false (blur).
         */
        atualizarFoco(state, foco) {
            state.focoJanela = foco
        },

        /**
         * Atualiza a condição "mobile" do widget.
         */
        updateMobile(state, isMobile) {
            state.mobile = isMobile
        },

        /**
         * Atualiza o objeto inteiro de aparência. Essa função é geralmente chamada pelo editor do Web messenger quando o usuário muda
         * algum parâmetro, como tamanho do convite ou cor primária.
         */
        atualizarAparência(state, aparência) {
            state.aparência = aparência
            if (aparência.maxAmountOfActiveRooms) {
                state.maxAmountOfActiveRooms = aparência.maxAmountOfActiveRooms
            }
        },

        /**
         * Atualiza o fuso horário do workspace.
         */
        updateTimezoneOffset(state, timezoneOffset) {
            state.timezoneOffset = timezoneOffset
        },

        /**
         * Atualiza o horário de trabalho.
         */
        updateWorkingHours(state, workingHours) {
            state.workingHours = workingHours
        },

        /**
         * Atualiza o status de visibilidade do convite.
         */
        setInviteVisibility(state, isVisible) {
            state.inviteVisible = isVisible
        },

        /**
         * Atualiza a qtde. máxima de atendimentos por grupo.
         */
        updateMaxActiveTicketsPerGroup(state, value) {
            state.maxActiveTicketsPerGroup = value
        },

        /**
         * Atualiza o atributo de agrupamento dos tickets.
         */
        updateGroupTicketsBy(state, value) {
            state.groupTicketsBy = value
        },

        /**
         * Atualiza o status de disponibilidade dos departamentos.
         */
        updateDepartmentsAvailability(state, availabilityMap) {
            state.departmentsAvailability = availabilityMap
        },

        /**
         * Atualiza o status de conexão de conexão da websocket.
         */
        updateWebsocketConnectionStatus(state, status) {
            state.websocketConnectionStatus = status
        },

        /**
         * Atualiza a qtde. máxima de atendimentos simultâneos que o contato pode abrir.
         */
        updateMaxAmountOfActiveRooms(state, n) {
            state.maxAmountOfActiveRooms = n
        },

        /**
         * Atualiza a opção de iniciar atendimentos somente por chamadas de API.
         */
        updateOnlyAllowNewChatRoomsFromAPICalls(state, only) {
            state.onlyAllowNewChatRoomsFromAPICalls = only
        },

        /**
         * Atualiza o termo de pesquisa na base de conhecimento.
         * A alteração desse estado é vigiada pela view que executa a pesquisa de fato.
         */
        updateSearch(state, search) {
            state.search = search
            persistirEstado()
        },

        /**
         * Atualiza os conhecimentos encontrados na última pesquisa realizada pelo usuário.
         */
        updateFoundKnowledgeDatabaseQuestions(state, questions) {
            state.questionsFoundFromKnowledgeDatabaseSearch = questions
            persistirEstado()
        },

        /**
         * Atualiza o ID do conhecimento selecionado.
         */
        selectQuestion(state, questionId) {
            state.selectedQuestionId = questionId
            persistirEstado()
        },

        /**
         * Atualiza a lista de departamentos que o usuário pode iniciar conversas.
         */
        atualizarDepartamentos(state, departamentos) {
            state.departamentos = departamentos
        },

        /**
         * Atualiza a flag 'openingNewRoom' que atualiza o estado do widget para carregando.
         */
        updateOpeningNewRoom(state, flag) {
            state.openingNewRoom = flag
        },

        /**
         * Decrementa a posição na fila da sala especificada.
         */
        decrementQueuePosition(state, dados) {
            let rooms = state.salasByTab['chat'].filter(room => {
                return room.department_id == dados.department_id
            })

            rooms.forEach(room => {
                let shouldDecrement = true

                if (dados.room_id) {
                    if (dados.room_id > room.id) {
                        shouldDecrement = false
                    }
                }

                if (shouldDecrement) {
                    let index = state.queuePositions[room.id] || 0
                    Vue.set(state.queuePositions, room.id, Math.max(index - 1, 0))
                }
            })

            persistirEstado()
        },

        /**
         * Atualiza a posição da fila para esse atendimento.
         */
        updateQueuePosition(state, { room_id, queue_position }) {
            Vue.set(state.queuePositions, room_id, queue_position)
            persistirEstado()
        },

        /**
         * Alguns dados são salvos de forma persistente no navegador da pessoa, como as salas que ela tem aberta, o seu registro
         * de contato e informações que já sabemos do perfil, como nome e e-mail.
         *
         * Esses dados ficam salvos e são recuperados quando a pessoa acessa o site novamente.
         */
        restoreFromSerializedState(state, serialized) {
            if (serialized.etapa) state.etapa = serialized.etapa == 'thank_you_after_close' ? 'home' : serialized.etapa
            if (serialized.maximizeStep) state.maximizeStep = serialized.maximizeStep
            if (serialized.queuePositions) state.queuePositions = serialized.queuePositions

            if (serialized.salaSelecionada) state.salaSelecionada = serialized.salaSelecionada
            if (serialized.salasByTab) state.salasByTab = serialized.salasByTab
            if (serialized.contato) state.contato = serialized.contato
            if (serialized.search) state.search = serialized.search

            if (serialized.questionsFoundFromKnowledgeDatabaseSearch) {
                state.questionsFoundFromKnowledgeDatabaseSearch = serialized.questionsFoundFromKnowledgeDatabaseSearch
            }

            if (serialized.selectedQuestionId) state.selectedQuestionId = serialized.selectedQuestionId
        },

        /**
         * Atualiza a etapa (view) do widget.
         */
        atualizarEtapa(state, etapa) {
            state.etapa = etapa

            if (etapa !== 'conversa') {
                state.salaSelecionada = null
            }

            if (etapa !== 'convite') {
                state.maximizeStep = null
            }

            persistirEstado()
        },

        /**
         * Atualiza a etapa que será maximizada após o usuário clicar no convite.
         */
        updateMaximizeStep(state, maximizeStep) {
            state.maximizeStep = maximizeStep
        },

        /**
         * Registro do contato que representa a pessoa que está acessando o website.
         */
        updateContact(state, contato) {
            state.contato = contato
            persistirEstado()
        },

        /**
         * Adiciona uma nova sala de conversa no cache e seleciona essa sala.
         */
        adicionarSalaESelecionar(state, sala) {
            state.salasByTab['chat'].push(sala)
            state.salaSelecionada = sala
            persistirEstado()
        },

        /**
         * Atualiza a sala de conversa selecionada.
         */
        selecionarSala(state, sala) {
            state.etapa = 'conversa'
            state.salaSelecionada = sala
            persistirEstado()
        },

        iniciarConversaApósIdentificação(state, departamentoToken) {
            let departamento = store.getters.findDepartamentByToken(departamentoToken)

            state.etapa = 'conversa'
            state.salaSelecionada = {
                temporário: new Date().getTime(),
                departamento_token: departamentoToken,
                department_id: departamento.id,
            }

            if (state.perfil.first_message) {
                store.dispatch('abrirAtendimentoOuEnviarMensagemParaSalaSelecionada', {
                    corpo_html: state.perfil.first_message,
                    corpo_texto: state.perfil.first_message,
                })
            }

            persistirEstado()
        },

        /**
         * Atualiza o perfil da pessoa que está acessando o perfil.
         */
        atualizarPerfil(state, { chave, valor }) {
            Vue.set(state.perfil, chave, valor)
            persistirEstado()
        },

        atualizarPerfilDoUser(state, { canal, perfil }) {
            Vue.set(state.perfisDosUsers, canal, perfil)
        },

        'flash/removerFlash'(state, { chave }) {
            console.log('clear flash typing')
            state.flashTyping = {}
        },
    },
    actions: {
        ...chatStore.actions,

        initFullscreen({ commit, state }) {
            state.fullscreen = true
            if (state.etapa == 'convite') {
                commit('atualizarEtapa', 'home')
            }
        },

        /**
         * Recebemos um alerta de digitando do operador.
         */
        async updateFlashTyping({ state }, { key, data }) {
            let latestFlashCall = new Date().getTime()
            window._latestFlashCall = latestFlashCall

            Vue.set(state.flashTyping, key, data)

            await new Promise(resolve => setTimeout(resolve, 3000))

            if (latestFlashCall == window._latestFlashCall) {
                Vue.delete(state.flashTyping, key)
            }
        },

        forceInviteOnLoad({ commit }) {
            commit('atualizarEtapa', 'convite')
        },

        /**
         * Atualiza o foco da janela. Nós precisamos desse controle para marcar mensagens como lida.
         */
        atualizarFoco({ commit, state, dispatch }, foco) {
            commit('atualizarFoco', foco)

            fastlane('chat:change_window_focus', {
                focus: foco,
            })

            if (foco && state.salaSelecionada && state.salaSelecionada.id) {
                dispatch('talvezMarcarEventosDaSalaSelecionadaComoLidos')
            }
        },

        /**
         * Se o widget estiver no convite, nós devemos abrir a última tela aberto. Se não estiver no convite, nós devemos
         * ir para o convite.
         */
        minimize({ commit, state }) {
            commit('updateMaximizeStep', { step: state.etapa, room: state.salaSelecionada })
            commit('atualizarEtapa', 'convite')

            persistirEstado()
        },

        /**
         * Quando o usuário clicar no convite, nós devemos:
         *
         * 1. Abrir a identificação, caso não tenha uma conversa em atendimento.
         * 2. Maximizar a conversa, caso tenha uma conversa em andamento.
         */
        clicarNoConvite({ commit, dispatch, getters, state }) {
            if (state.mobile) {
                return window.open(
                    state.endpoint +
                        '/api/chat/web_messengers.html?token=' +
                        state.webMessengerToken +
                        '&fullscreen=true',
                    '_blank'
                )
            }

            if (typeof state.maximizeStep === 'string') {
                commit('atualizarEtapa', state.maximizeStep || 'home')
            } else if (state.maximizeStep && 'room' in state.maximizeStep && state.maximizeStep.room) {
                dispatch('selecionarSala', state.maximizeStep.room)
                commit('atualizarEtapa', state.maximizeStep.step)
            } else {
                commit('atualizarEtapa', 'home')
            }

            commit('updateMaximizeStep', null)

            // if (state.etapa == 'conversa' && getters.salaSelecionada) {
            //     dispatch('selecionarSala', getters.salaSelecionada)
            // }

            persistirEstado()
        },

        transiçãoParaDireita({ commit, state }, view) {
            commit('atualizarEtapa', {
                from: state.etapa,
                to: view,
                transition: 'left-to-right',
            })

            setTimeout(() => {
                commit('atualizarEtapa', view)
            }, 300)
        },

        transiçãoParaEsquerda({ commit, state }, view) {
            commit('atualizarEtapa', {
                from: state.etapa,
                to: view,
                transition: 'right-to-left',
            })

            setTimeout(() => {
                commit('atualizarEtapa', view)
            }, 300)
        },

        /**
         * Faz uma requisição HTTP para buscar a disponibilidade dos departamentos desse widget.
         */
        async loadDepartamentsAvailability({ state, dispatch }) {
            if (state.maxActiveTicketsPerGroup && state.groupTicketsBy) {
                await dispatch('loadDepartamentsAvailabilityFromApi')
            } else {
                await dispatch('loadDepartamentsAvailabilityFromJson')
            }
        },

        async loadDepartamentsAvailabilityFromJson({ commit, state, dispatch }) {
            return axios
                .get(
                    state.endpoint + '/available_maps/' + state.webMessengerToken + '.json?time=' + new Date().getTime()
                )
                .then(res => {
                    commit('updateDepartmentsAvailability', res.data)
                })
                .catch(err => {
                    dispatch('loadDepartamentsAvailabilityFromApi')
                })
        },

        async loadDepartamentsAvailabilityFromApi({ commit, state }) {
            let { data } = await axios.get(state.endpoint + '/api/core/departments/available_to_chat', {
                params: {
                    ...state.perfil,
                    max_active_tickets_per_group: state.maxActiveTicketsPerGroup,
                    group_tickets_by: state.groupTicketsBy,
                    ids: state.departamentos.map(d => d.id),
                },
            })

            commit('updateDepartmentsAvailability', data)
        },

        /**
         * Seleciona uma sala e carrega os eventos caso seja o primeiro acesso.
         */
        async selecionarSala({ commit, dispatch, state, getters }, sala) {
            commit('selecionarSala', sala)

            if ((sala.id && !getters.eventosDaSala(sala.id)) || getters.eventosDaSala(sala.id).loading) {
                dispatch('carregarEventosDaSala', {
                    id_cliente: sala.id_cliente,
                    id_sala: sala.id,
                    room_token: sala.token,
                    channel: 'crm_contacts:' + state.contato.id,
                    retryIfEmpty: true,
                })
            }

            setTimeout(() => {
                // Nós precisamos emitir o evento dentro de um timeout.

                document.dispatchEvent(
                    new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala: sala.id, force: true } })
                )
            }, 100)

            dispatch('talvezMarcarEventosDaSalaSelecionadaComoLidos')
        },

        /**
         * Adiciona uma nova sala de conversa no cache e seleciona essa sala.
         */
        async adicionarSalaESelecionar({ commit, state }, sala) {
            commit('adicionarSalaESelecionar', sala)
            return await abrirConexãoWebsocket(state.fastlaneEndpoint, state.contato, store)
        },

        /**
         * Envia uma nova mensagem para uma sala que está aberta, e nós já estamos conectado na Websocket - ou inicia
         * uma nova conversa caso seja a primeira mensagem.
         */
        async abrirAtendimentoOuEnviarMensagemParaSalaSelecionada({ commit, getters, state, dispatch }, dadosDoEnvio) {
            if (state.salaSelecionada.temporário) {
                commit('updateOpeningNewRoom', true)

                let openingData = {
                    departamento_token: state.salaSelecionada.departamento_token,
                    web_messenger_token: state.webMessengerToken,
                    message: dadosDoEnvio.corpo_html,
                    message_html: dadosDoEnvio.corpo_html,
                    message_text: dadosDoEnvio.corpo_texto,
                    max_active_tickets_per_group: state.maxActiveTicketsPerGroup,
                    group_tickets_by: state.groupTicketsBy,
                    ...state.perfil,
                }

                if (state.contato && state.salasByTab['chat'].length > 0) {
                    openingData.contact_id = state.contato.id
                }

                try {
                    let { chat_room, contact, queue_position } = await iniciarAtendimento(state.endpoint, openingData)

                    commit('updateOpeningNewRoom', false)
                    commit('updateContact', contact)
                    commit('updateQueuePosition', { room_id: chat_room.id, queue_position })

                    await dispatch('adicionarSalaESelecionar', chat_room)
                    ensureConnectedToDepartment(store, chat_room.department_id)

                    dispatch('carregarEventosDaSala', {
                        id_cliente: chat_room.id_cliente,
                        id_sala: chat_room.id,
                        room_token: chat_room.token,
                        channel: 'crm_contacts:' + contact.id,
                        retryIfEmpty: true,
                    })

                    setTimeout(() => dispatch('verificarConsistenciaCanalWebsocket'), 4000)
                } catch (err) {
                    commit('updateDepartmentsAvailability', {})
                    commit('atualizarEtapa', 'home')
                }
            } else {
                if (
                    !getters.salaSelecionada.atributos_extras.users_ids ||
                    !getters.salaSelecionada.atributos_extras.users_ids.length
                ) {
                    axios
                        .get(
                            state.endpoint +
                                '/api/chat/rooms/' +
                                getters.salaSelecionada.token +
                                '?id_cliente=' +
                                state.contato.id_cliente,
                            {
                                params: {
                                    id: getters.salaSelecionada.id,
                                    id_cliente: state.contato.id_cliente,
                                },
                            }
                        )
                        .then(res => {
                            commit('updateChatRoom', res.data)
                        })
                }

                dispatch('enviarMensagem', {
                    ...dadosDoEnvio,
                    autor_type: 'crm_contacts',
                    autor_id: state.contato.id,
                    id_cliente: getters.salaSelecionada.id_cliente,
                    mensageiro_type: 'chat_salas',
                    mensageiro_id: getters.salaSelecionada.id,
                })
            }
        },

        /**
         * Após o atendimento ser aberto, nós devemos verificar se o canal do contato atual está consistente
         * com o último contato retornado pelo servidor.
         */
        async verificarConsistenciaCanalWebsocket({ commit, state }) {
            console.log('Verificando consistência...')
            if (!state.salaSelecionada) return

            let { contacts_ids } = await fastlane('chat:room_contacts_ids', {
                id: state.salaSelecionada.id,
                token: state.salaSelecionada.token,
            })

            if (canalContato && contacts_ids && contacts_ids.length > 0) {
                let contactId = contacts_ids.shift()
                let expectedChannelName = 'crm_contacts:' + contactId

                if (canalContato.channelName != expectedChannelName) {
                    console.log('O canal está errado!')
                    commit('updateContact', { ...state.contato, id: contactId })
                    abrirConexãoWebsocket(state.fastlaneEndpoint, { id: contactId }, store)
                } else {
                    console.log('O canal está correto.')
                }
            }
        },

        async restoreFromSerializedState({ commit, dispatch, state, getters }, estadoSerializado) {
            commit('restoreFromSerializedState', estadoSerializado)

            if (!estadoSerializado.salasByTab) estadoSerializado.salasByTab = {}

            if (estadoSerializado.salasByTab['chat'].length > 0) {
                console.log(estadoSerializado.salasByTab['chat'])
                await dispatch('revalidateChatRooms')

                // Se mesmo depois de revalidar ainda existirem salas, nós podemos nos conectar à websocket
                // do contato e dos departamentos.
                if (estadoSerializado.salasByTab['chat'].length > 0) {
                    await abrirConexãoWebsocket(state.fastlaneEndpoint, state.contato, store)
                    state.salasByTab['chat'].forEach(room => {
                        ensureConnectedToDepartment(store, room.department_id)
                    })
                    dispatch('carregarEventosNãoLidos')
                }
            }

            if (estadoSerializado.salaSelecionada && !estadoSerializado.maximizeStep) {
                let roomFromStore = getters.findRoomById(estadoSerializado.salaSelecionada.id)
                if (roomFromStore) {
                    dispatch('selecionarSala', estadoSerializado.salaSelecionada)
                } else {
                    if (state.etapa == 'conversa') {
                        commit('atualizarEtapa', 'home')
                    }
                }
            }

            dispatch('verificarConsistenciaCanalWebsocket')
        },

        /**
         * Atualiza o registro da sala no servidor.
         */
        updateChatRoom({ state }, { room, data }) {
            return axios.put(state.endpoint + '/api/chat/rooms/' + room.token, data)
        },

        async revalidateChatRooms({ commit, state }) {
            let deleted = []

            for (let i = 0; i < state.salasByTab['chat'].length; i++) {
                let sala = state.salasByTab['chat'][i]

                let { data } = await axios.get(
                    state.endpoint + '/api/chat/rooms/' + sala.token + '?id_cliente=' + state.contato.id_cliente
                )

                if (!data) {
                    deleted.push(sala.id)
                } else if (!data.status) {
                    deleted.push(sala.id)
                } else if (data.status == 'closed' || data.status == 'deleted' || data.status == 'spam') {
                    deleted.push(data.id)
                } else {
                    commit('updateChatRoom', data)
                }
            }

            deleted.forEach(id => commit('deleteChatRoom', id))

            persistirEstado()
        },

        /**
         * Requisição para API para carregar o perfil do usuário para mostrar no header da conversa.
         */
        async loadUserProfile({ commit, state }, user_id) {
            let canal = 'core_users:' + user_id

            if (state.loadingUserProfiles[canal]) {
                return
            }

            state.loadingUserProfiles[canal] = true

            let { data } = await axios.get(state.endpoint + '/api/traits/profiles/users/' + user_id)
            commit('atualizarPerfilDoUser', { canal, perfil: data })

            state.loadingUserProfiles[canal] = false
        },

        /**
         * Pesquisa por conhecimentos baseado no termo digitado pelo usuário (state.search).
         * Os conhecimentos encontrados são armazenados no estado global e serializados no localStorage para
         * abrir rapidamente caso o usuário recarregue a página.
         */
        async searchForQuestions({ commit, state }) {
            let id_database = state.aparência.baseDeConhecimento
            let query = encodeURIComponent(state.search)

            commit('updateFoundKnowledgeDatabaseQuestions', null)

            let { data } = await axios.get(`/api/bdc/searches?id_database=${id_database}&query=${query}`)

            commit('updateFoundKnowledgeDatabaseQuestions', data)
        },
    },
})

export function perfil(canal) {
    let perfil = store.getters['perfil'](canal)
    if (perfil) return perfil

    if (canal.indexOf('core_users:') !== -1) {
        store.dispatch('loadUserProfile', canal.replace('core_users:', ''))
    } else if (canal.indexOf('crm_contacts:') !== -1) {
        store.dispatch('perfil/loadContactProfile', canal.replace('crm_contacts:', ''))
    }

    return { nome: __('Carregando...') }
}

export function userProfile(idUser) {
    if (!idUser) return {}

    let perfil = store.getters['perfil']('core_users:' + idUser)
    if (perfil) return perfil

    store.dispatch('loadUserProfile', idUser)
    return {}
}

/**
 * Salva o estado relevante do Widget no localStorage.
 *
 * @return {void}
 */
function persistirEstado() {
    Teclia.__postMessage('salvarEstado', {
        dados: {
            etapa: store.state.etapa,
            maximizeStep: store.state.maximizeStep,
            salasByTab: store.state.salasByTab,
            salaSelecionada: store.state.salaSelecionada,
            queuePositions: store.state.queuePositions,
            contato: store.state.contato,
            search: store.state.search,
            questionsFoundFromKnowledgeDatabaseSearch: store.state.questionsFoundFromKnowledgeDatabaseSearch,
            selectedQuestionId: store.state.selectedQuestionId,
        },
    })
}

async function iniciarAtendimento(endpoint, perfil) {
    let { data } = await axios.post(endpoint + '/api/chat/web_messengers/rooms', perfil)
    return data
}

function sleep(n) {
    return new Promise(resolve => setTimeout(resolve, n))
}
